import React from 'react';
import { Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SettingsIcon from '@mui/icons-material/Settings';
import { useTranslation } from 'react-i18next';

import SizeToggle from './SizeToggle';
import { availableGroups, groupOf } from './sizes';

/** Drag handle (whole element is the activator). */
export function DragHandle({ frame, children, sx, ...rest }) {
  const { t } = useTranslation();
  const handle = frame.handleProps || {};
  return (
    <Box
      ref={handle.setActivatorNodeRef}
      {...handle.attributes}
      {...handle.listeners}
      tabIndex={0}
      role="button"
      aria-label={t('dashboard.edit.dragHandle', { title: frame.title })}
      data-testid={`widget-${frame.id}-header`}
      onKeyDown={frame.onKeyDown}
      sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1, minWidth: 0, cursor: 'grab', outline: 'none', '&:focus-visible': { boxShadow: 'inset 0 0 0 2px #3182ce', borderRadius: '6px' }, ...sx }}
      {...rest}
    >
      <DragIndicatorIcon sx={{ fontSize: 20, color: '#94a3b8', flexShrink: 0 }} />
      {children}
    </Box>
  );
}

/** Size toggle + settings + remove, shared by the edit header and the tile control row. */
export function EditActions({ frame }) {
  const { t } = useTranslation();
  const { entry, id, size } = frame;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
      <SizeToggle groups={availableGroups(entry)} active={groupOf(size)} onChange={frame.onResizeGroup} widgetId={id} />
      {entry.settings?.length ? (
        <IconButton
          size="small"
          data-testid={`widget-${id}-settings`}
          aria-label={t('dashboard.edit.settings')}
          onClick={(event) => {
            event.stopPropagation();
            frame.onOpenSettings(event.currentTarget);
          }}
          sx={{ color: '#64748b', p: 0.5 }}
        >
          <SettingsIcon sx={{ fontSize: 17 }} />
        </IconButton>
      ) : null}
      <IconButton
        size="small"
        data-testid={`widget-${id}-remove`}
        aria-label={t('dashboard.edit.remove')}
        onClick={(event) => {
          event.stopPropagation();
          frame.onRemove();
        }}
        sx={{ color: '#64748b', p: 0.5, '&:hover': { color: '#c53030' } }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
}

/** Edit-mode 40 px control row for headless tiles (replaces the icon row). */
export function TileControlRow({ frame }) {
  return (
    <Box sx={{ height: 40, flexShrink: 0, display: 'flex', alignItems: 'center', pl: 0.75, pr: 1.25, gap: 1, borderBottom: '1px solid #f1f5f9' }}>
      <DragHandle frame={frame}>
        <Box component="span" sx={{ fontSize: 12.5, fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {frame.title}
        </Box>
      </DragHandle>
      <EditActions frame={frame} />
    </Box>
  );
}

/** Edit-mode placeholder for a widget that currently has nothing to show (one grid row). */
export function HiddenRow({ frame }) {
  const { t } = useTranslation();
  return (
    <Box data-testid={`widget-${frame.id}-hidden`} sx={{ height: '100%', display: 'flex', alignItems: 'center', pl: 0.75, pr: 1.25, gap: 1 }}>
      <DragHandle frame={frame} sx={{ gap: 1 }}>
        <Box component="span" sx={{ fontSize: 13, fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {frame.title}
        </Box>
        <Box component="span" sx={{ fontSize: 12.5, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {t('dashboard.edit.hiddenNote')}
        </Box>
      </DragHandle>
      <EditActions frame={frame} />
    </Box>
  );
}
